<style lang="less" scoped src="@/assets/less/manage-space/base.less"></style>

<template>
    <div class="pl-sm-4 mt-4">
        <CoCard variant="normal" class="mb-3">
            <div class="d-flex w-100 justify-content-between align-items-start">
                <CoHeadline :level="2">{{ $t('labels.connectnukiaccount') }}</CoHeadline>
                <CoPill v-if="isConnected" :text="$t('labels.connected')" variant="success" iconleft="check-circle" />
                <CoButton
                    v-else
                    :disabled="loading"
                    :label="$t('labels.connect')"
                    :loading="loading"
                    :variant="'primary'"
                    @click="getNukiOAuth2Url"
                />
            </div>
            <CoText class="mt-4 mb-0">{{ $t('messages.nukiImportDescription') }}</CoText>
        </CoCard>

        <CoCard variant="normal" class="mb-3">
            <CoHeadline :level="2">{{ $t('labels.locks') }}</CoHeadline>
            <div class="d-flex align-items-center justify-content-between mt-3">
                <CoInput
                    v-model="search"
                    type="search"
                    :disabled="!isConnected || loading || locksLoading"
                    :placeholder="$t('placeholders.search')"
                    background="white"
                    class="mr-2"
                    icon="search"
                />
                <CoButton
                    :disabled="!isConnected || loading || locksLoading"
                    :label="$t('labels.synchronize')"
                    :loading="locksLoading"
                    :title="$t('labels.synchronize')"
                    variant="secondary"
                    @click="importLocks"
                />
            </div>
            <CoTable
                :columns="[
                    { key: 'Name', title: $t('labels.name'), sortable: true },
                    { key: 'Provider', title: $t('labels.provider'), sortable: true },
                ]"
                :items="filteredItems"
                :itemsPerPage="10"
                :loading="locksLoading"
                auto-items-per-page
                resizable-columns
            />
        </CoCard>
    </div>
</template>

<script>
import { get } from 'lodash';
import axios from 'axios';
import EventBus from '@/eventBus';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoCard from '@/components/Molecules/co-card/CoCard.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import CoTable from '@/components/Molecules/co-table/CoTable.vue';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import CoText from '@/components/Atoms/co-text/CoText.vue';

export default {
    name: 'NukiIntegration',
    components: {
        CoButton,
        CoCard,
        CoInput,
        CoTable,
        CoHeadline,
        CoText,
    },
    data() {
        return {
            loading: false,
            locksLoading: false,
            isConnected: false,
            objects: [],
            search: '',
        };
    },
    computed: {
        filteredItems() {
            const searchTerm = this.search.toLowerCase();
            return this.objects.filter((item) =>
                [item.Name, item.Provider].some((value) =>
                    String(value || '')
                        .toLowerCase()
                        .includes(searchTerm)
                )
            );
        },
    },
    methods: {
        getNukiOAuth2Url() {
            // immediately open a new window to avoid popup blockers
            const authWindow = window.open('', '_blank');
            this.loading = true;
            axios({
                method: 'GET',
                url: `/admin/oauth2/nuki`,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response && response.data) {
                        // update the url of the popup window
                        authWindow.location.href = response.data.url;
                    }
                })
                .catch((error) => {
                    let message = this.$t('messages.nukiAuthUrlError');
                    if (authWindow === null) {
                        message = this.$t('messages.popupBlocked');
                    } else {
                        authWindow.close();
                    }
                    EventBus.$emit('ERROR', {
                        message,
                        Details: error,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        isNukiConnected() {
            this.loading = true;
            axios({
                method: 'GET',
                url: `/admin/oauth2/nuki/verify`,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    this.isConnected = get(response, 'data.connected', false);
                    if (this.isConnected) {
                        // Load Nuki locks only after the account connection has been verified.
                        this.listLocks();
                    } else {
                        this.objects = [];
                    }
                })
                .catch((error) => {
                    EventBus.$emit('ERROR', {
                        Message: this.$t('messages.nukiVerifyFailed'),
                        Details: error,
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        listLocks() {
            if (!this.isConnected) {
                return;
            }

            this.locksLoading = true;
            axios
                .get('/admin/space/access/lock/list')
                .then((response) => {
                    this.objects = (response && response.data ? response.data : []).filter((item) => {
                        if (item.Provider === 'NUKI' || item.Provider === 'nuki') {
                            return true;
                        }
                        return false;
                    });
                    if (this.objects.length == 0) {
                        this.importLocks();
                    }
                })
                .catch((error) => {
                    EventBus.$emit('ERROR', {
                        message: this.$t('messages.nukiVerifyFailed'),
                        Details: error,
                    });
                })
                .finally(() => {
                    this.locksLoading = false;
                });
        },
        importLocks() {
            if (!this.isConnected) {
                return;
            }

            this.locksLoading = true;
            axios
                .get('/admin/space/access/lock/import')
                .then((response) => {
                    this.objects = response && response.data ? response.data : [];
                })
                .catch((error) => {
                    EventBus.$emit('ERROR', {
                        message: this.$t('messages.nukiVerifyFailed'),
                        Details: error,
                    });
                })
                .finally(() => {
                    this.locksLoading = false;
                });
        },
    },
    mounted() {
        this.isNukiConnected();
    },
};
</script>
